import React, { useState } from 'react';
import { Archive, LogOut, UserPlus } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  useArchiveCollaboration,
  useCollaboration,
  useLeaveCollaboration,
  useRemoveCollaborationDoctor,
} from '../hooks/useCollaborationQueries';
import InviteDoctorDialog from './InviteDoctorDialog';
import MemberRoster from './MemberRoster';
import NoteComposer from './NoteComposer';
import CollaborationTimeline from './CollaborationTimeline';
import { useOptimisticCollaborationNotes } from '../hooks/useOptimisticCollaborationNotes';

export function CollaborationWorkspace({ collaborationId, onClose }) {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removingDoctorId, setRemovingDoctorId] = useState(null);
  const detail = useCollaboration(collaborationId);
  const leave = useLeaveCollaboration();
  const archive = useArchiveCollaboration();
  const removeDoctor = useRemoveCollaborationDoctor();
  const collaboration = detail.data;
  const notes = useOptimisticCollaborationNotes(collaborationId);

  if (detail.isLoading) return <div className="collab-state">Loading collaboration…</div>;
  if (detail.isError || !collaboration) return <div className="collab-state collab-state--error">Unable to load this collaboration.</div>;

  const act = async (mutation, label) => {
    if (!window.confirm(`${label} this collaboration?`)) return;
    try {
      await mutation.mutateAsync({ collaborationId });
      toast.success(`Collaboration ${label.toLowerCase()}d.`);
      onClose?.();
    } catch (error) {
      toast.error(error?.message || `Unable to ${label.toLowerCase()} collaboration.`);
    }
  };

  const removeMember = async (member) => {
    const doctor = member.doctor;
    if (!doctor || !window.confirm(`Remove ${doctor.name} from this collaboration? They will lose access immediately.`)) return;
    setRemovingDoctorId(doctor.id);
    try {
      await removeDoctor.mutateAsync({ collaborationId, doctorId: doctor.id });
      toast.success(`${doctor.name} removed from the collaboration.`);
    } catch (error) {
      toast.error(error?.message || 'Unable to remove this doctor.');
    } finally {
      setRemovingDoctorId(null);
    }
  };

  return (
    <section className="collab-workspace" aria-labelledby="collab-workspace-title">
      <section className="collab-workspace__information" aria-label="Collaboration information">
        <div className="collab-workspace__header">
          <div>
            <button type="button" className="collab-back" onClick={onClose}>← Back to collaborations</button>
            <p className="collab-eyebrow">Shared patient workspace</p>
            <h2 id="collab-workspace-title">{collaboration.patient?.name}</h2>
            <p>{collaboration.reason}</p>
          </div>
          <div className="collab-workspace__members">
            {collaboration.canManageMembers && <button type="button" className="collab-button collab-button--ghost" onClick={() => setInviteOpen(true)}><UserPlus size={16} /> Invite doctor</button>}
            {collaboration.canLeave && <button type="button" className="collab-button collab-button--danger" onClick={() => act(leave, 'Leave')} disabled={leave.isPending}><LogOut size={16} /> Leave</button>}
            {collaboration.canArchive && <button type="button" className="collab-button collab-button--ghost" onClick={() => act(archive, 'Archive')} disabled={archive.isPending}><Archive size={16} /> Archive</button>}
          </div>
        </div>
        <MemberRoster members={collaboration.members || []} canManageMembers={collaboration.canManageMembers} ownerId={collaboration.inviter?.id} onRemove={removeMember} removingDoctorId={removingDoctorId} />
      </section>
      {inviteOpen && <InviteDoctorDialog collaborationId={collaborationId} members={collaboration.members || []} onClose={() => setInviteOpen(false)} />}

      {collaboration.viewerAccess === 'active' || !collaboration.viewerAccess
        ? <CollaborationTimeline collaborationId={collaborationId} outgoing={notes.outgoing} onRetry={notes.retry} />
        : <div className="collab-state">Accept the invitation to see shared notes.</div>}

      {collaboration.status === 'archived' ? (
        <div className="collab-notice">This workspace is archived and remains available as read-only history.</div>
      ) : collaboration.viewerAccess === 'active' || !collaboration.viewerAccess
        ? <NoteComposer collaborationId={collaboration.id} onSend={notes.send} />
        : null}
    </section>
  );
}

export default CollaborationWorkspace;
